(() => {
  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN';
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const textValue = (value) => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (value && typeof value === 'object') return String(value.name ?? value.value ?? '');
    return '';
  };

  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const buildQuickBuy = (root) => {
    const overlay = make('div', 'imeta__quick');
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');

    const backdrop = make('button', 'imeta__quick-backdrop');
    backdrop.type = 'button';
    backdrop.setAttribute('aria-label', 'Cerrar opciones');
    backdrop.dataset.imetaQuickClose = '';

    const sheet = make('div', 'imeta__quick-sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Elegir opciones del producto');

    const close = make('button', 'imeta__quick-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Cerrar');
    close.dataset.imetaQuickClose = '';

    const productWrap = make('div', 'imeta__quick-product');
    const image = make('img', 'imeta__quick-image');
    image.alt = '';
    image.loading = 'eager';
    const productCopy = make('div', 'imeta__quick-copy');
    productCopy.appendChild(make('p', 'imeta__eyebrow', 'ELIGE TU VARIANTE'));
    const title = make('h3', 'imeta__quick-title');
    const price = make('p', 'imeta__quick-price');
    productCopy.append(title, price);
    productWrap.append(image, productCopy);

    const form = make('form', 'imeta__quick-form');
    form.noValidate = true;
    const optionsWrap = make('div', 'imeta__quick-options');
    const status = make('p', 'imeta__quick-status', 'Selecciona todas las opciones.');
    status.setAttribute('aria-live', 'polite');
    const add = make('button', 'imeta__button imeta__quick-add', 'Agregar al carrito');
    add.type = 'submit';
    add.disabled = true;
    const details = make('a', 'imeta__quick-details', 'Ver detalles del producto');
    form.append(optionsWrap, status, add, details);

    const success = make('div', 'imeta__quick-success');
    success.hidden = true;
    success.appendChild(make('strong', '', 'Agregado al carrito'));
    success.appendChild(make('p', '', 'La pieza ya está en tu carrito.'));
    const successActions = make('div', 'imeta__quick-success-actions');
    const cartLink = make('a', 'imeta__button', 'Ir al carrito');
    cartLink.href = '/cart';
    const keepShopping = make('button', 'imeta__quick-secondary', 'Seguir comprando');
    keepShopping.type = 'button';
    keepShopping.dataset.imetaQuickClose = '';
    successActions.append(cartLink, keepShopping);
    success.appendChild(successActions);

    sheet.append(close, productWrap, form, success);
    overlay.append(backdrop, sheet);
    root.appendChild(overlay);

    return { overlay, sheet, image, title, price, form, optionsWrap, status, add, details, success, cartLink };
  };

  const init = (root) => {
    if (!root || root.dataset.imetaQuickReady === 'true') return;
    root.dataset.imetaQuickReady = 'true';

    const ui = buildQuickBuy(root);
    const cache = new Map();
    let activeTrigger = null;
    let activeProduct = null;
    let activeVariant = null;
    let selects = [];
    let availableVariants = [];
    let optionDefinitions = [];

    const variantOptions = (variant) => {
      if (Array.isArray(variant.options)) return variant.options.map(textValue);
      return optionDefinitions.map((_, index) => textValue(variant[`option${index + 1}`]));
    };

    const variantOptionsFallback = (variant, index) => {
      if (Array.isArray(variant.options)) return textValue(variant.options[index]);
      return textValue(variant[`option${index + 1}`]);
    };

    const normalizeOptions = (product) => {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const rawOptions = Array.isArray(product.options) ? product.options : [];
      const maxVariantOptions = variants.reduce((max, variant) => {
        const count = Array.isArray(variant.options) ? variant.options.length : [variant.option1, variant.option2, variant.option3].filter(Boolean).length;
        return Math.max(max, count);
      }, 0);
      const count = Math.max(rawOptions.length, maxVariantOptions, 1);

      return Array.from({ length: count }, (_, index) => {
        const raw = rawOptions[index];
        const name = typeof raw === 'string' ? raw : textValue(raw && raw.name) || `Opción ${index + 1}`;
        let values = raw && typeof raw === 'object' && Array.isArray(raw.values) ? raw.values.map(textValue) : [];
        if (!values.length) values = variants.map((variant) => variantOptionsFallback(variant, index)).filter(Boolean);
        return { name, values: [...new Set(values)] };
      });
    };

    const resetUi = () => {
      ui.form.hidden = false;
      ui.success.hidden = true;
      ui.status.textContent = 'Selecciona todas las opciones.';
      ui.status.classList.remove('is-error', 'is-success');
      ui.add.disabled = true;
      ui.add.textContent = 'Agregar al carrito';
      ui.optionsWrap.replaceChildren();
      selects = [];
      activeVariant = null;
    };

    const closeQuick = () => {
      if (ui.overlay.hidden) return;
      ui.overlay.hidden = true;
      ui.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('imeta-quick-open');
      if (activeTrigger) activeTrigger.focus({ preventScroll: true });
      activeTrigger = null;
      activeProduct = null;
      activeVariant = null;
    };

    const focusables = () => [...ui.sheet.querySelectorAll('button:not([disabled]), a[href], select:not([disabled])')]
      .filter((node) => !node.hidden && node.offsetParent !== null);

    ui.sheet.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const nodes = focusables();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const exactVariant = () => {
      const selected = selects.map((select) => select.value);
      if (!selected.length || selected.some((value) => !value)) return null;
      return availableVariants.find((variant) => variantOptions(variant).every((value, index) => value === selected[index])) || null;
    };

    const syncOptions = () => {
      if (!selects.length) return;
      const selected = selects.map((select) => select.value);
      let resetNeeded = false;

      selects.forEach((select, optionIndex) => {
        [...select.options].forEach((option) => {
          if (!option.value) return;
          const candidate = selected.slice();
          candidate[optionIndex] = option.value;
          option.disabled = !availableVariants.some((variant) => {
            const values = variantOptions(variant);
            return values.every((value, index) => !candidate[index] || value === candidate[index]);
          });
        });
        if (select.value && select.selectedOptions[0] && select.selectedOptions[0].disabled) {
          select.value = '';
          resetNeeded = true;
        }
      });

      if (resetNeeded) {
        syncOptions();
        return;
      }

      activeVariant = exactVariant();
      ui.add.disabled = !activeVariant;
      ui.status.classList.remove('is-error', 'is-success');

      if (activeVariant) {
        ui.price.textContent = money(activeVariant.price);
        ui.status.textContent = 'Opción disponible. Lista para agregar.';
      } else if (selects.every((select) => select.value)) {
        ui.status.textContent = 'Esta combinación no está disponible. Elige otra opción.';
        ui.status.classList.add('is-error');
      } else {
        ui.status.textContent = 'Selecciona todas las opciones.';
      }
    };

    const renderProduct = (product, trigger) => {
      resetUi();
      activeProduct = product;
      optionDefinitions = normalizeOptions(product);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      availableVariants = variants.filter((variant) => variant.available);

      ui.title.textContent = product.title || 'Producto ISAMOR';
      ui.price.textContent = money(product.price || (availableVariants[0] && availableVariants[0].price) || 0);
      const imageUrl = product.featured_image || (Array.isArray(product.images) && product.images[0]) || '';
      if (imageUrl) {
        ui.image.src = imageUrl;
        ui.image.alt = product.title || 'Producto ISAMOR';
        ui.image.hidden = false;
      } else {
        ui.image.removeAttribute('src');
        ui.image.hidden = true;
      }
      ui.details.href = trigger.href;
      ui.cartLink.href = '/cart';

      optionDefinitions.forEach((definition, index) => {
        const field = make('div', 'imeta__quick-field');
        const label = make('label', 'imeta__quick-label', definition.name);
        const select = make('select', 'imeta__quick-select');
        select.dataset.imetaOption = String(index);
        select.setAttribute('aria-label', definition.name);
        const placeholder = make('option', '', `Selecciona ${definition.name.toLocaleLowerCase('es-MX')}`);
        placeholder.value = '';
        select.appendChild(placeholder);
        definition.values.forEach((value) => {
          const option = make('option', '', value);
          option.value = value;
          select.appendChild(option);
        });
        label.htmlFor = `imeta-quick-${index}-${Math.random().toString(36).slice(2)}`;
        select.id = label.htmlFor;
        select.addEventListener('change', syncOptions);
        field.append(label, select);
        ui.optionsWrap.appendChild(field);
        selects.push(select);
      });

      syncOptions();
    };

    const openQuick = async (trigger) => {
      activeTrigger = trigger;
      resetUi();
      ui.overlay.hidden = false;
      ui.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('imeta-quick-open');
      ui.title.textContent = 'Cargando opciones…';
      ui.price.textContent = '';
      ui.image.hidden = true;
      ui.details.href = trigger.href;
      ui.status.textContent = 'Cargando disponibilidad…';
      requestAnimationFrame(() => ui.sheet.querySelector('[data-imeta-quick-close]')?.focus());

      try {
        const url = new URL(trigger.href, window.location.origin);
        const endpoint = `${url.pathname.replace(/\/$/, '')}.js`;
        let product = cache.get(endpoint);
        if (!product) {
          const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('No se pudieron cargar las opciones.');
          product = await response.json();
          cache.set(endpoint, product);
        }
        renderProduct(product, trigger);
        requestAnimationFrame(() => (selects[0] || ui.add).focus());
      } catch (_) {
        closeQuick();
        window.location.href = trigger.href;
      }
    };

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('a.imeta__button--small');
      if (trigger && root.contains(trigger) && trigger.textContent.trim().toLocaleLowerCase('es-MX') === 'elegir opciones') {
        event.preventDefault();
        openQuick(trigger);
        return;
      }

      const closer = event.target.closest('[data-imeta-quick-close]');
      if (closer && ui.overlay.contains(closer)) {
        event.preventDefault();
        closeQuick();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !ui.overlay.hidden) closeQuick();
    });

    ui.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!activeVariant || !activeProduct) return;

      ui.add.disabled = true;
      ui.add.textContent = 'Agregando…';
      ui.status.textContent = 'Agregando tu pieza al carrito…';
      ui.status.classList.remove('is-error', 'is-success');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(activeVariant.id), quantity: 1 }] })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'No se pudo agregar la pieza.');

        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          root.querySelectorAll('.imeta__cart').forEach((cartNode) => {
            cartNode.textContent = `Carrito (${cart.item_count})`;
          });
        }

        window.dispatchEvent(new CustomEvent('isamor:quickbuy-added', {
          detail: { productId: activeProduct.id, productHandle: activeProduct.handle, variantId: activeVariant.id, price: activeVariant.price }
        }));

        ui.form.hidden = true;
        ui.success.hidden = false;
        requestAnimationFrame(() => ui.cartLink.focus());
      } catch (error) {
        ui.add.disabled = false;
        ui.add.textContent = 'Agregar al carrito';
        ui.status.textContent = error.message || 'No se pudo agregar la pieza. Intenta de nuevo.';
        ui.status.classList.add('is-error');
      }
    });
  };

  const boot = () => document.querySelectorAll('.imeta').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();